import React from 'react';
import AdUnit from './AdUnit';
import { getAdSlot } from './adConfig';

interface MultiplexAdProps {
  aiFocus?: boolean;
  showLabel?: boolean;
  adCount?: 1 | 2 | 3;
  layout?: 'vertical' | 'horizontal' | 'grid';
  testMode?: boolean;
  compact?: boolean;
}

const MultiplexAd: React.FC<MultiplexAdProps> = ({
  aiFocus = true,
  showLabel = true,
  adCount = 1,
  layout = 'vertical',
  testMode = false,
  compact = false
}) => {
  const slotId = getAdSlot('multiplex');

  return (
    <div className={`w-full my-8 flex justify-center ${compact ? 'my-4' : ''}`}>
      <div className={`w-full ${layout === 'horizontal' ? 'max-w-[728px]' : 'max-w-[336px]'}`}>
        {showLabel && (
          <div className={`text-xs text-gray-500 mb-2 text-center ${aiFocus ? 'flex items-center justify-center gap-2' : ''}`}>
            {aiFocus ? (
              <>
                <span className="inline-flex items-center">
                  <svg className="w-3 h-3 mr-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                  </svg>
                  AI Tools Selection
                </span>
                {adCount > 1 && (
                  <span className="text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                    {adCount} AI Solutions
                  </span>
                )}
              </>
            ) : (
              'Advertisement'
            )}
          </div>
        )}

        <div className="ad-multiplex-container">
          <AdUnit
            slot={slotId}
            format={layout === 'horizontal' ? 'leaderboard' : 'rectangle'}
            style={{
              width: '100%',
              minHeight: layout === 'horizontal' ? '90px' : (compact ? '250px' : '280px'),
              margin: '0 auto',
              ...(aiFocus && {
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }),
              ...(testMode && { border: '2px dashed #f59e0b' })
            }}
            className={`multiplex-ad ${layout} ${aiFocus ? 'ai-contextual' : ''}`}
          />
        </div>
        
        {aiFocus && adCount > 1 && (
          <div className="text-[10px] text-gray-400 mt-2 text-center">
            {adCount === 2 ? 'Showing complementary AI tools and platforms' :
             adCount === 3 ? 'Multiple AI solutions and development tools' :
             'AI technology recommendations'}
          </div>
        )}
      </div>
    </div>
  );
};

// Keep the named exports as they are
export const SingleAIAd: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <MultiplexAd 
    aiFocus={true}
    adCount={1}
    layout="vertical"
    showLabel={true}
    compact={compact}
  />
);

export const DualAIAd: React.FC<{ layout?: 'vertical' | 'horizontal' }> = ({ layout = 'vertical' }) => (
  <MultiplexAd 
    aiFocus={true}
    adCount={2}
    layout={layout}
    showLabel={true}
  />
);

export const TripleAIAd: React.FC<{ layout?: 'vertical' | 'grid' }> = ({ layout = 'vertical' }) => (
  <MultiplexAd 
    aiFocus={true}
    adCount={3}
    layout={layout}
    showLabel={true}
  />
);

export const HorizontalAIAd: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <MultiplexAd 
    aiFocus={true}
    adCount={1}
    layout="horizontal"
    showLabel={true}
    compact={compact}
  />
);

export const AIAdGrid: React.FC<{ adCount?: 2 | 3 }> = ({ adCount = 2 }) => (
  <MultiplexAd 
    aiFocus={true}
    adCount={adCount}
    layout="grid"
    showLabel={true}
  />
);

export default MultiplexAd;
